import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OnboardingService } from '../../../services/onboarding/onboarding.service';
import { isFieldInvalid,validateFile } from '../../shared/validators/form-validation.utils';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';

@Component({
  selector: 'app-da-recommendations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    FieldErrorComponent
  ],
  providers: [MessageService],
  templateUrl: './recommendations.component.html',
  styleUrl: './recommendations.component.scss'
})
export class DaRecommendationsComponent implements OnInit {
  @Input() parentForm!: FormGroup;

  displayPreviewDialog: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  originalFileUrl: string = '';
  isPdfLoading: boolean = false;
  isSavingMap: { [key: number]: boolean } = {};

  // Delete dialog state
  showDeleteDialog = false;
  isDeleting = false;
  pendingDeleteIndex: number | null = null;

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    if (!this.parentForm.get('recommendations')) {
      this.parentForm.addControl('recommendations', this.fb.array([]));
    }

    this.loadRecommendationsData();
  }

  get recommendations(): FormArray {
    return this.parentForm.get('recommendations') as FormArray;
  }

  loadRecommendationsData() {
    this.onboardingService.getRecommendations().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || []);
        if (list && list.length > 0) {
          this.recommendations.clear();
          list.forEach((record: any) => {
            this.addRecommendation(record);
          });
          while (this.recommendations.length < 1) {
            this.addRecommendation();
          }
        } else {
          // Add at least 1 defaults if empty
          while (this.recommendations.length < 1) {
            this.addRecommendation();
          }
        }
      },
      error: (err) => {
        console.warn('Failed to load recommendations:', err);
        while (this.recommendations.length < 1) {
          this.addRecommendation();
        }
      }
    });
  }

  addRecommendation(data?: any) {
    const group = this.fb.group({
      id: [data?.id || null],
      name: [data?.name || '', FieldValidators.personName],
      organisation: [data?.organisationName || '', FieldValidators.alphanumericText],
      email: [data?.officialEmail || '', FieldValidators.email],
      contactNumber: [data?.contactNumber || '', FieldValidators.mobile10],
      fileUrl: [data?.lorUrl || null]
    });
    this.recommendations.push(group);
  }

  removeRecommendation(index: number) {
    const id = this.recommendations.at(index).get('id')?.value;
    if (!id) {
      if (this.recommendations.length > 1) {
        this.recommendations.removeAt(index);
      } else {
        this.recommendations.at(index).reset();
      }
      return;
    }
    this.pendingDeleteIndex = index;
    this.showDeleteDialog = true;
  }

  confirmDelete() {
    if (this.pendingDeleteIndex === null) return;
    const index = this.pendingDeleteIndex;
    const id = this.recommendations.at(index).get('id')?.value;
    this.isDeleting = true;
    this.onboardingService.deleteRecommendation(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        if (this.recommendations.length > 1) {
          this.recommendations.removeAt(index);
        } else {
          this.recommendations.at(index).reset();
        }
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Recommender removed successfully.' });
      },
      error: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete recommender.' });
      }
    });
  }

  cancelDelete() {
    this.showDeleteDialog = false;
    this.pendingDeleteIndex = null;
  }

  saveRecommendation(index: number) {
    const recGroup = this.recommendations.at(index) as FormGroup;
    recGroup.markAllAsTouched();
    if (recGroup.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError();
      return;
    }

    const val = recGroup.value;
    const existingId = val.id;

    const formData = new FormData();
    if (existingId) {
      formData.append('StudentRecommendationId', existingId.toString());
    }

    formData.append('Name', val.name || '');
    formData.append('OrganisationName', val.organisation || '');
    formData.append('OfficialEmail', val.email || '');
    formData.append('ContactNumber', val.contactNumber || '');

    const selectedFile = (recGroup as any)._selectedFile;
    if (selectedFile) {
      formData.append('LORUrl', selectedFile);
    }

    if (existingId) {

      if (!recGroup.dirty && !selectedFile) {
        this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
        return;
      }
      this.isSavingMap[index] = true;
      this.onboardingService.updateRecommendation(existingId, formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Recommendation record updated successfully.' });
          recGroup.markAsPristine();
        },
        error: (err) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update recommendation record.' });
        }
      });
    } else {
      this.isSavingMap[index] = true;
      this.onboardingService.createRecommendation(formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          const newId = res?.id || res?.result?.id;
          if (newId) {
            recGroup.get('id')?.setValue(newId);
          }
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Recommendation record saved successfully.' });
          recGroup.markAsPristine();
        },
         error: (err) => {
  this.isSavingMap[index] = false;
  const backendMsg = err?.error?.errors?.documentUrl?.[0] || 'Failed to update award.';
  this.messageService.add({ severity: 'error', summary: 'Error', detail: backendMsg });
}
      });
    }
  }

onFileSelected(event: any, index: number, inputRef: HTMLInputElement) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const check = validateFile(file);
  if (!check.valid) {
    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
    inputRef.value = '';
    return;
  }

  const recGroup = this.recommendations.at(index) as FormGroup;
  (recGroup as any)._selectedFile = file;
  const localUrl = URL.createObjectURL(file);
  recGroup.get('fileUrl')?.setValue(localUrl);
  recGroup.get('fileUrl')?.markAsDirty();
  inputRef.value = '';
}

  getFileName(url: string | null): string {
    if (!url) return 'Upload LOR';
    try {
      const decoded = decodeURIComponent(url);
      return decoded.split('/').pop()?.split('?')[0] || 'Document';
    } catch {
      return 'Document';
    }
  }

  previewFile(url: string, name: string = 'Document Preview') {
    if (!url) return;
    this.originalFileUrl = url;
    this.previewFileName = name;
    this.isPdfLoading = true;
    // Blob URLs are local (file selected but not saved yet) — open directly
    // Remote URLs route through Google Docs Viewer
    let viewerUrl: string;
    if (url.startsWith('blob:')) {
      viewerUrl = url;
    } else {
      viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    }
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.displayPreviewDialog = true;
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
